import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, DialogContent, DialogTrigger } from "@/shared/ui/dialog"
import { Button } from "@/shared/ui/button"
import { useTranslation } from "react-i18next";
import AddReport from '@/features/generation/AddReport';
import RecordingDiscardDialog from '@/features/recording/RecordingDiscardDialog';
import { useCurrentTool } from "@/shared/hooks/useCurrentTool"
import { selectFileUploading, selectRecordingStarted, selectRecordingDuration, selectReportId, setRecordingStarted, setRecordingDuration, setReportId } from '@/store/slices/ReportsSlice'
import { createRecordingLog } from '@/api/imperative/recording'
import { useReportsContext } from '@/features/reports/ReportsContext';

function AddReportDialog({
  variant = "mobile",
  className = ""
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { toolInputs, generation_name } = useCurrentTool();

  const fileUploading = useSelector(selectFileUploading);
  const recordingStarted = useSelector(selectRecordingStarted);
  const reocordingDuration = useSelector(selectRecordingDuration);
  const reportId = useSelector(selectReportId);

  const { isAddOpen, setIsAddOpen, activeDraft, setActiveDraft, refreshDrafts, recordingSessionRef } = useReportsContext();
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const closeAddDialog = useCallback(({ skipRecordingCleanup = false } = {}) => {
    setIsAddOpen(false);
    if (activeDraft) setActiveDraft(null);
    refreshDrafts();
    if (!skipRecordingCleanup && recordingStarted) {
      createRecordingLog({
        type: 'discard',
        duration: reocordingDuration,
        reportId,
        createdAt: new Date().toISOString()
      });
      dispatch(setRecordingStarted(false));
      dispatch(setRecordingDuration(0));
      dispatch(setReportId(null));
    }
  }, [setIsAddOpen, activeDraft, setActiveDraft, refreshDrafts, recordingStarted, reocordingDuration, reportId, dispatch]);

  const handleAddDialog = (open) => {
    if (fileUploading) return;
    if (open) {
      setIsAddOpen(true);
      return;
    }
    if (recordingSessionRef?.current?.isActive) {
      setDiscardDialogOpen(true);
      return;
    }
    closeAddDialog();
  };

  const handleDiscardConfirm = async () => {
    setDiscardDialogOpen(false);
    await recordingSessionRef?.current?.abandon?.();
    closeAddDialog({ skipRecordingCleanup: true });
  };

  const isMobile = variant === "mobile";
  const isDesktop = variant === "desktop";

  return (
    <>
      <Dialog
        className={`${isMobile ? 'md:hidden' : 'hidden md:flex'} ${className}`}
        open={isAddOpen}
        onOpenChange={handleAddDialog}
      >
        <DialogTrigger asChild className={isMobile ? '' : 'hidden md:flex'}>
          <Button disabled={!toolInputs.length}>
            <div className="text-[14px]">
              <span className={`${isMobile ? 'text-[16px]' : 'text-[20px]'} mr-1`}>+</span>
              {t('reports.add.text', { type: generation_name })}
            </div>
          </Button>
        </DialogTrigger>
        <DialogContent
          className={`${isDesktop ? 'md:h-fit h-full shadow-none rounded-[25px]' : 'md:hidden'}`}
          addingReport
          fileUploading={fileUploading}
        >
          <AddReport activeDraft={activeDraft} setActiveDraft={setActiveDraft} />
        </DialogContent>
      </Dialog>

      <RecordingDiscardDialog
        open={discardDialogOpen}
        onOpenChange={setDiscardDialogOpen}
        onConfirm={handleDiscardConfirm}
      />
    </>
  );
}

export default AddReportDialog;
